import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button as ChakraButton,
  Center,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  InputGroup,
  PinInput,
  PinInputField,
  RadioGroup,
  Skeleton,
  SkeletonText,
  Spinner,
  Stack,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import {
  Button,
  Footer,
  Heading,
  Input,
  Select,
  StatusBadge,
  Table,
} from '~/components';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { RadioCard } from '~/components/RadioCard';
import { validateNotificationEmail } from '~/utils/emailRegex';
import { useContractExtensionProposal } from '~/hooks/useExtensions/useExtensions';
import {
  getGenerateContractExtensionToken,
  getSetContractExtensionToken,
} from '~/services/Extensions';
import { CloseContractForm } from '~/pages/Portal/Contract/CloseContractForm';
import { ContractExtensionProposal, Signer } from '~/types/extensionProposal';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';

export const ContractExtension: React.VFC = () => {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [signers, setSigners] = useState<Signer[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingVerifyToken, setIsFetchingVerifyToken] = useState(false);
  const [voltar, setVoltar] = useState(false);

  const [radio, setRadio] = useState<undefined | string>(undefined);
  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();
  const {
    isOpen: isOpenModalTermo,
    onOpen: onOpenModalTermo,
    onClose: onCloseModalTermo,
  } = useDisclosure();
  const [codigo, setCodigo] = useState<string>('');

  const options = [
    { value: 1, label: 'Assinante' },
    { value: 2, label: 'Testemunha' },
    { value: 3, label: 'Com cópia' },
  ];

  const history = useHistory();
  const { data, isLoading } = useContractExtensionProposal(id);

  const getResponsaveis = ({ envolvidos }: ContractExtensionProposal) => {
    return envolvidos.map(envolvido => ({
      id: envolvido.id,
      nome: envolvido.name,
      email: envolvido.email,
      tipo: envolvido.responsavel ? '1' : '',
    }));
  };

  const handleDiscount = (id_discount: string | undefined) => {
    clearErrors('desconto');
    setRadio(id_discount);
  };

  useEffect(() => {
    if (data) {
      setSigners(getResponsaveis(data));
    }
  }, [data]);

  const handleNewRowSignerList = () => {
    clearErrors('signers');
    setSigners([
      ...signers,
      {
        id: uuid(),
        nome: '',
        email: '',
        tipo: '3',
      } as unknown as Signer,
    ]);
  };

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;

  const handleCreate = () => {
    if (!validate()) {
      return;
    }
    const formData = {
      id_desconto: radio,
      responsaveis: signers,
    };

    setIsFetching(true);
    const request = api.put(`assinatura_extensao/${id}`, formData);
    request
      .then(() => {
        toast({
          description: 'Proposta de extensão enviada com sucesso.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries(['contract', Number(id)]);
        openModalTermo();
      })
      .catch(err => {
        toast({
          title: 'Erro',
          description: err.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  const handleDeleteSigner = (signerId: number, index: number) => {
    clearErrors(`nome-${index}`);
    clearErrors(`email-${index}`);
    setSigners(signers.filter(item => item.id !== signerId));
  };

  const handleNomeEdit = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    clearErrors(`nome-${index}`);
    setSigners(
      signers.map((signer: Signer, i) => {
        if (i === index) {
          return {
            ...signer,
            nome: e.target.value,
          };
        }
        return signer;
      }),
    );
  };

  const handleEmailEdit = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    clearErrors(`email-${index}`);
    setSigners(
      signers.map((signer: Signer, i) => {
        if (i === index) {
          return {
            ...signer,
            email: e.target.value,
          };
        }
        return signer;
      }),
    );
  };

  const handleTipoEdit = (
    e: React.ChangeEvent<HTMLSelectElement>,
    index: number,
  ) => {
    clearErrors(`tipo-${index}`);
    setSigners(
      signers.map((signer: Signer, i) => {
        if (i === index) {
          return {
            ...signer,
            tipo: e.target.value,
          };
        }
        return signer;
      }),
    );
  };

  const validate = () => {
    if (!existeTestemunhaEAssinante()) {
      toast({
        title: 'Erro',
        description:
          'É necessário ter pelo menos um assinante e uma testemunha.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return false;
    }
    clearErrors();

    !signers.length &&
      setError('signers', {
        type: 'required',
        message: 'Inclua pelo menos um contato.',
      });

    signers.forEach((signer, index) => {
      if (signer.nome == null || signer.nome.trim() === '') {
        setError(`nome-${index}`, {
          type: 'required',
          message: 'Campo obrigatório',
        });
      }

      if (signer.nome.length > 255) {
        setError(`nome-${index}`, {
          type: 'maxLength',
          message: 'Nome deve ter no máximo 255 caracteres.',
        });
      }

      if (signer.email.length > 255) {
        setError(`email-${index}`, {
          type: 'maxLength',
          message: 'E-mail deve ter no máximo 255 caracteres.',
        });
      }

      if (signer.email == null || signer.email.trim() === '') {
        setError(`email-${index}`, {
          type: 'required',
          message: 'Campo obrigatório',
        });
      }

      if (signer.email) {
        if (!validateNotificationEmail(signer.email)) {
          setError(`email-${index}`, {
            type: 'pattern',
            message: 'E-mail inválido',
          });
        }
      }

      if (!signer.tipo) {
        setError(`tipo-${index}`, {
          type: 'required',
          message: 'Campo obrigatório',
        });
      }

      const repetido = signers.find(
        (p, i) => p.email === signer.email && i !== index,
      );
      if (repetido) {
        setError(`email-${index}`, {
          type: 'pattern',
          message: 'E-mail repetido',
        });
      }
    });

    !radio &&
      setError('desconto', {
        type: 'required',
        message: 'Selecione um desconto',
      });

    const { _formState } = control;
    return _formState.isValid;
  };

  const handleCodigoChange = (value: any) => {
    setCodigo(value);
  };

  const existeTestemunhaEAssinante = () => {
    return (
      signers.some(signer => signer.tipo === '2') &&
      signers.some(signer => signer.tipo === '1')
    );
  };

  const confirmar = () => {
    setIsFetchingVerifyToken(true);
    getSetContractExtensionToken(data!.proposta_extensao!.id, codigo, id)
      .then(() => {
        handleCreate();
      })
      .catch(err => {
        toast({
          title: 'Erro',
          description: err.response.data,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetchingVerifyToken(false);
      });
  };

  const openModal = () => {
    if (!validate()) {
      return;
    }
    setIsFetchingVerifyToken(true);
    getGenerateContractExtensionToken(data!.proposta_extensao!.id)
      .then(() => {
        onOpenModal();
      })
      .catch(err => {
        toast({
          title: 'Erro',
          description: err.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetchingVerifyToken(false);
      });
  };

  const reeviarToken = () => {
    setIsFetching(true);
    getGenerateContractExtensionToken(data!.proposta_extensao!.id)
      .then(() => {
        toast({
          title: 'Sucesso',
          description: 'Token reenviado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setIsFetching(false);
      })
      .catch(err => {
        toast({
          title: 'Erro',
          description: err.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const closeModalTermo = () => {
    setVoltar(true);
    onCloseModalTermo();
  };
  const openModalTermo = () => {
    onCloseModal();
    onOpenModalTermo();
  };

  useEffect(() => {
    if (voltar) {
      history.goBack();
    }
  }, [voltar]);

  if (isLoading)
    return (
      <Box px="8" py="4">
        <Skeleton width="30%" height="20px" />
        <Flex
          flexDir="row"
          flexWrap="wrap"
          w="100%"
          py={9}
          gridGap="12"
          style={{
            position: 'relative',
          }}
        >
          <Stack maxW={450} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
          <Center
            position="relative"
            bottom="0"
            display={{ md: 'block', sm: 'none' }}
          />
          <Stack maxW={450} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
          <Center
            position="relative"
            bottom="0"
            display={{ md: 'block', sm: 'none' }}
          />
          <Stack maxW={450} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
        <Flex flexFlow="column" maxW="1000px">
          <Stack>
            <Skeleton width="200px" height="20px" />
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
        <Flex flexFlow="column" maxW="700px" mt="8">
          <Stack>
            <Skeleton width="200px" height="20px" />
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
      </Box>
    );

  return (
    <>
      <Box px="8" py="4" pb="8rem">
        <Stack gridGap={5}>
          <Heading hasBackButton>
            Estender Contrato N° {data?.nu_contrato}
          </Heading>
          <Divider orientation="horizontal" />
          <Flex
            flexDir="row"
            flexWrap="wrap"
            w="100%"
            gridGap="12"
            style={{
              position: 'relative',
            }}
          >
            <Stack maxW={450} w="100%" gridGap={4}>
              <Flex justify="space-between" align="center">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  N° termo/pedido de locação:
                </Text>
                <Text color="gray.500" fontSize="14px">
                  {data?.nu_pedido}
                </Text>
              </Flex>
              <Flex justify="space-between" align="center">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  N° meses total:
                </Text>
                <Text color="gray.500" fontSize="14px">
                  {data?.nu_mesescontrato}
                </Text>
              </Flex>
            </Stack>
            <Stack maxW={450} w="100%" gridGap={4}>
              <Flex justify="space-between" align="center">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  Data de encerramento:
                </Text>
                <Text color="gray.500" fontSize="14px">
                  {data?.formattedDataVencimento}
                </Text>
              </Flex>
              <Flex justify="space-between" align="center">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  Valor mensal:
                </Text>
                <Text color="gray.500" fontSize="14px">
                  {data?.formattedValue}
                </Text>
              </Flex>
            </Stack>
            <Stack maxW={450} w="100%" gridGap={4}>
              <Flex justify="space-between" align="baseline">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  Mensalidades em aberto:
                </Text>
                <Text color="gray.500" fontSize="14px" textAlign="right">
                  {data?.qtde_faturas_em_aberto}
                </Text>
              </Flex>
              <Flex justify="space-between" align="baseline">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  Status de pagamento:
                </Text>
                <Text color="gray.500" fontSize="14px" textAlign="right">
                  <StatusBadge variant="green">{data?.status_pgto}</StatusBadge>
                </Text>
              </Flex>
            </Stack>
          </Flex>
          {!!data?.equipamentos.length && (
            <Flex flexFlow="column" maxW="880px">
              <Flex
                py={2}
                w="100%"
                justifyContent="space-between"
                alignItems="baseline"
              >
                <Heading fontSize="xl">Equipamentos locados</Heading>
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  Total de equipamentos: {data?.qtde_total_equipamentos}
                </Text>
              </Flex>
              <Table type="secondary">
                <Thead>
                  <Tr>
                    <Th w="30%">SKU</Th>
                    <Th w="60%">Modelo</Th>
                    <Th>Qtd</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.equipamentos.map(equipamento => (
                    <Tr key={equipamento.id_equipamento}>
                      <Td>{equipamento.cd_equipamento}</Td>
                      <Td>{equipamento.nm_modeloequipamento || '-'}</Td>
                      <Td>{equipamento.qtde}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Flex>
          )}
          <Stack>
            <Heading>Valores para extensão</Heading>
            <FormControl isInvalid={errors.desconto}>
              <RadioGroup value={radio} onChange={setRadio}>
                <HStack maxW="1080px" overflowX="scroll">
                  {data?.discounts.map(discount => {
                    return (
                      <RadioCard
                        discount={discount}
                        checkBoxValue={radio}
                        handleDiscountSelected={handleDiscount}
                        data={data}
                      />
                    );
                  })}
                </HStack>
              </RadioGroup>
              {errors.desconto && (
                <FormErrorMessage>{errors.desconto?.message}</FormErrorMessage>
              )}
            </FormControl>

            <Alert
              bg="#ffffff"
              borderRadius="md"
              color="#9246ce"
              fontSize="sm"
              py={1}
              px={2}
              mx={2}
            >
              <AlertTitle>Atenção:</AlertTitle>
              <AlertDescription>
                <Text color="black">
                  Os contratos continuam com sua cobrança normal até a data de
                  encerramento. Os descontos serão aplicados no mês de início da
                  extensão.
                </Text>
              </AlertDescription>
            </Alert>
          </Stack>
          <Stack maxW="1080px">
            <Flex
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack>
                <Heading fontSize="xl" mb={0}>
                  Envolvidos no contrato
                </Heading>
                <Text>
                  Informe quem deve receber o termo de extensão do contrato para
                  assinatura.
                </Text>
              </Stack>
              <Button
                border="1px solid"
                borderColor="#939291"
                onClick={handleNewRowSignerList}
              >
                Adicionar
              </Button>
            </Flex>
            <FormControl isInvalid={errors.signers}>
              {signers.length && !isLoading ? (
                <Stack>
                  <Table type="secondary">
                    <Thead>
                      <Tr>
                        <Th>Nome</Th>
                        <Th style={{ textTransform: 'none' }}>E-mail</Th>
                        <Th>Tipo</Th>
                        <Th>Ações</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {signers.map((signer, index) => (
                        <Tr key={signer.id}>
                          <Td>
                            <FormControl isInvalid={errors[`nome-${index}`]}>
                              <Input
                                name="nome"
                                type="text"
                                error={errors[`nome-${index}`]}
                                fontSize="15px"
                                size="sm"
                                value={signer.nome}
                                onChange={e => handleNomeEdit(e, index)}
                                mb={
                                  (errors[`tipo-${index}`] ||
                                    errors[`email-${index}`]) &&
                                  !errors[`nome-${index}`]
                                    ? '29px'
                                    : '0'
                                }
                              />
                            </FormControl>
                          </Td>
                          <Td>
                            <FormControl isInvalid={errors[`email-${index}`]}>
                              <Input
                                name="email"
                                type="text"
                                error={errors[`email-${index}`]}
                                fontSize="15px"
                                size="sm"
                                value={signer.email}
                                onChange={e => handleEmailEdit(e, index)}
                                mb={
                                  (errors[`tipo-${index}`] ||
                                    errors[`nome-${index}`]) &&
                                  !errors[`email-${index}`]
                                    ? '29px'
                                    : '0'
                                }
                              />
                            </FormControl>
                          </Td>
                          <Td>
                            <FormControl isInvalid={errors[`type-${index}`]}>
                              <Select
                                name="tipo"
                                error={errors[`tipo-${index}`]}
                                defaultValue={signer.tipo}
                                onChange={e => handleTipoEdit(e, index)}
                                fontSize="15px"
                                mb={
                                  !errors[`tipo-${index}`] &&
                                  (errors[`nome-${index}`] ||
                                    errors[`email-${index}`])
                                    ? '29px'
                                    : '0'
                                }
                              >
                                {signer.tipo === '' && (
                                  <option key="" value="">
                                    &nbsp;
                                  </option>
                                )}
                                {options?.map(tipo => (
                                  <option key={tipo.value} value={tipo.value}>
                                    {tipo.label}
                                  </option>
                                ))}
                              </Select>
                            </FormControl>
                          </Td>
                          <Td>
                            <ChakraButton
                              color="#5b8af0"
                              variant="link"
                              onClick={() =>
                                handleDeleteSigner(signer.id, index)
                              }
                            >
                              Excluir
                            </ChakraButton>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  <Alert
                    bg="#ffffff"
                    borderRadius="md"
                    color="#9246ce"
                    fontSize="sm"
                    py={1}
                    px={2}
                    mx={2}
                  >
                    <AlertTitle>Atenção:</AlertTitle>
                    <AlertDescription>
                      <Text color="black">
                        Para assinantes, é necessário ter procuração.
                      </Text>
                    </AlertDescription>
                  </Alert>
                </Stack>
              ) : (
                <Flex justify="center" py={4}>
                  <Text>Nenhum envolvido adicionado.</Text>
                </Flex>
              )}
              {errors.signers && (
                <FormErrorMessage>{errors.signers?.message}</FormErrorMessage>
              )}
            </FormControl>
          </Stack>
        </Stack>
      </Box>
      <Footer justify="space-between">
        <HStack>
          {data && data.proposta_extensao && (
            <CloseContractForm
              id_contrato={id}
              id_proposta={data.proposta_extensao.id}
              data_encerramento={data.formattedDataVencimento}
            />
          )}
        </HStack>
        <HStack>
          <Button
            variant="secondary"
            onClick={() => history.goBack()}
            isDisabled={isFetching}
          >
            Cancelar
          </Button>
          <Button type="submit" isDisabled={isFetching} onClick={openModal}>
            Enviar para aprovação
            {(isFetching || isFetchingVerifyToken) && (
              <Spinner style={{ marginLeft: '10px' }} />
            )}
          </Button>
        </HStack>
      </Footer>
      <ConfirmationDialog
        titulo="Enviamos um token para o seu e-mail"
        onClose={onCloseModal}
        confirmar={confirmar}
        isOpen={isOpenModal}
        isDisabled={codigo.length < 6 || isFetching || isFetchingVerifyToken}
        btnConfirmText="Finalizar"
      >
        <Text>
          Para confirmar o envio da extensão para aprovação, insira o código
          para finalizar:
        </Text>
        <FormControl isInvalid={errors.codigo}>
          <InputGroup justifyContent="start">
            <HStack>
              <PinInput
                size="lg"
                onChange={event => handleCodigoChange(event)}
                isDisabled={isFetching || isFetchingVerifyToken}
              >
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
              </PinInput>
            </HStack>
          </InputGroup>
          <HStack marginTop={4} justifyContent="space-between">
            <ChakraButton
              color="#5b8af0"
              variant="link"
              onClick={reeviarToken}
              fontSize="sm"
            >
              Reenviar token via e-mail{' '}
            </ChakraButton>
            {(isFetching || isFetchingVerifyToken) && <Spinner />}
          </HStack>
        </FormControl>
      </ConfirmationDialog>
      <ConfirmationDialog
        titulo="Obrigado!"
        onClose={closeModalTermo}
        confirmar={closeModalTermo}
        isOpen={isOpenModalTermo}
        hasCloseButton={false}
        btnConfirmText="Fechar"
      >
        <Text>
          Enviamos o termo para os envolvidos no contrato. Acompanhe o status no
          painel de Contratos.
        </Text>
      </ConfirmationDialog>
    </>
  );
};
